// The forced password change (SM-535).
//
// A newly provisioned admin, or one whose password a Super Admin has
// just reset, holds a credential their operator knows. Until they
// replace it, backoffice-service refuses every /admin/* route except
// this one and /me/permissions — so this screen is deliberately OUTSIDE
// the /_app shell: the shell's nav, dashboard and every panel in it
// would render and then 403 in place.
//
// It sits beside /login rather than inside the app for the same reason
// /login does: it is a gate, not a page.
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { fetchMeOrNull } from "@/lib/auth";
import { fetchMyPermissions, keys } from "@/lib/queries";
import { ChangePasswordForm } from "@/components/security/ChangePasswordForm";
import { SparkHorizontal } from "@/components/ui/SparkHorizontal";
import { LanguageSelector } from "@/components/i18n/LanguageSelector";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/change-password")({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData({
      queryKey: keys.me(),
      queryFn: fetchMeOrNull,
      staleTime: 5_000,
    });
    if (!me) throw redirect({ to: "/login" });
    // Reaching this screen without being gated is not an error — an
    // admin may bookmark it — but there is nothing to force, so send
    // them to Settings where the same form lives with its normal
    // chrome. A failed permission read leaves them here, which is the
    // safe direction: the form still works.
    const perms = await context.queryClient
      .ensureQueryData({ queryKey: keys.myPerms(), queryFn: fetchMyPermissions })
      .catch(() => null);
    if (perms && !perms.must_change_password) {
      throw redirect({ to: "/settings" });
    }
  },
  component: ChangePasswordPage,
});

function ChangePasswordPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <SparkHorizontal
              viewBox="243 632 1611 628"
              className="h-8 w-auto shrink-0 text-t-1"
            />
            <div className="text-2xs uppercase tracking-[0.18em] text-t-3">
              {t("common.backoffice")}
            </div>
          </div>
          <LanguageSelector />
        </div>
        <div className="modal">
          <div className="modal-glow" />
          <div className="modal-body space-y-5">
            <div>
              <div className="auth-eyebrow">{t("auth.operatorConsole")}</div>
              <h2 className="auth-headline">Set your own password</h2>
              <p className="auth-sub">
                The password you signed in with was issued by an
                administrator, so it is not private to you. Choose one only
                you know — the rest of the backoffice stays closed until
                you do.
              </p>
            </div>
            <ChangePasswordForm
              submitLabel="Set password and continue"
              onDone={async () => {
                // REFETCH, not invalidate. The gate is server-side, so
                // the cached /me/permissions payload is what _app's
                // beforeLoad reads next — and it reads it through
                // ensureQueryData, which returns existing data even
                // when that data is stale. Invalidating alone therefore
                // hands the router the pre-change payload, it sees
                // must_change_password still true, and it redirects
                // straight back here on top of a password that did
                // change. (Same trap login.tsx documents for keys.me().)
                //
                // Awaited on purpose: if the change somehow did NOT
                // clear the flag, bouncing back here is the right
                // outcome, and it should be decided on fresh data.
                await qc.refetchQueries({ queryKey: keys.myPerms() });
                navigate({ to: "/" });
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
